import {useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import {t} from 'shared/core';
import {Panel, Button, FormError} from 'shared/components';
import {Modal, ModalButtons} from 'shared/components/modals';
import {FormattedMessage} from 'react-intl';
import OAuthButtons from 'shared/components/login/OAuthButtons';

const PROVIDER_NAME = {
  google: 'Google',
  microsoft: 'Microsoft',
  zoom: 'Zoom'
};

const ConnectedWith = observer(({connectionType, onDisconnect}) => (
  <div className='mt2'>
    <Button onClick={onDisconnect} trait='destructive'>
      <FormattedMessage id='user_settings.scheduling_integrations.DISCONNECT_INTEGRATION' values={{provider: PROVIDER_NAME[connectionType]}}/>
    </Button>
  </div>
));

const DisconnectModal = observer(({isOpen, onCancel, onSave, titleId, instructionsId, error}) => {
  if (!isOpen) return null;

  return (
    <Modal size='md' isOpen={isOpen} onHide={onCancel} title={titleId}>
      <FormattedMessage id={instructionsId}/>
      <FormError message={error}/>
      <ModalButtons
        saveCaption='user_settings.scheduling_integrations.Disconnect'
        onCancel={onCancel}
        onSave={onSave}
        trait='destructive'
      />
    </Modal>
  );
});

const CalendarApp = observer(({uiState}) => {
  const {user} = uiState;
  const connectionType = user.calendarAppConnectionType;

  return (
    <div className='mb3'>
      <Panel.Subheader title={t('user_settings.scheduling_integrations.Calendar app')}/>
      <p className='mb1 jumbo'>{t('user_settings.scheduling_integrations.calendar_explainer')}</p>
      {connectionType ?
        <ConnectedWith
          connectionType={connectionType}
          onDisconnect={() => uiState.showCalendarDisconnectModal()}
        /> :
        <OAuthButtons context='connect_calendar_app' providers={['google', 'microsoft']}/>
      }
      <DisconnectModal
        isOpen={uiState.calendarDisconnectModalOpen}
        onCancel={() => uiState.hideCalendarDisconnectModal()}
        onSave={() => uiState.disconnectCalendarApp()}
        titleId='user_settings.scheduling_integrations.Disconnect calendar app'
        instructionsId='user_settings.scheduling_integrations.disconnect_calendar_instructions'
        error={uiState.disconnectError}
      />
    </div>
  );
});

const MeetingApp = observer(({uiState}) => {
  const {user} = uiState;
  const connectionType = user.meetingAppConnectionType;

  return (
    <div>
      <Panel.Subheader title={t('user_settings.scheduling_integrations.Meeting app')}/>
      <p className='mb1 jumbo'>{t('user_settings.scheduling_integrations.meeting_explainer')}</p>
      {connectionType ?
        <ConnectedWith
          connectionType={connectionType}
          onDisconnect={() => uiState.showMeetingDisconnectModal()}
        /> :
        <OAuthButtons context='connect_meeting_app' providers={['google', 'microsoft', 'zoom']}/>
      }
      <DisconnectModal
        isOpen={uiState.meetingDisconnectModalOpen}
        onCancel={() => uiState.hideMeetingDisconnectModal()}
        onSave={() => uiState.disconnectMeetingApp()}
        titleId='user_settings.scheduling_integrations.Disconnect meeting app'
        instructionsId='user_settings.scheduling_integrations.disconnect_meeting_instructions'
        error={uiState.disconnectError}
      />
    </div>
  );
});

const SchedulingIntegrations = observer(({uiState}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (window.location.hash === '#scheduling_integrations') {
      ref.current?.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
  }, []);

  return (
    <Panel>
      <div ref={ref}>
        <Panel.Header
          title={t('user_settings.scheduling_integrations.Scheduling Integrations')}
          description={t('user_settings.scheduling_integrations.explainer')}
        />
        <CalendarApp uiState={uiState}/>
        <MeetingApp uiState={uiState}/>
      </div>
    </Panel>
  );
});

export default SchedulingIntegrations;
